/**
 * App-weite Error-Boundary.
 *
 * Verhindert dass ein Render-Crash (z.B. unerwartetes JSON-Shape aus der DB)
 * die ganze App in eine schwarze Seite verwandelt. Stattdessen wird eine
 * verständliche Fehlermeldung gezeigt mit Reload-Button.
 *
 * Wird im main.tsx als äußerster Wrapper um <App /> gelegt.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { logCaughtError } from '@/lib/error-log';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });
    // eslint-disable-next-line no-console
    console.error('AppErrorBoundary caught:', error, errorInfo);
    // W95 (#141) — Render-Crashes landeten NUR in der Browser-Console
    // (Audit Agent C: "Frontend-Fehler laufen ins Leere"). Jetzt in
    // v5_error_log persistiert — anon-Insert-Policy existiert, Pfad
    // per Probe-Insert verifiziert.
    logCaughtError('app.error-boundary', error, {
      severity: 'critical',
      context: { component_stack: errorInfo.componentStack?.slice(0, 800) ?? null },
    });
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleHardReload = (): void => {
    // Hard-Reload: Browser-Cache + Service-Worker komplett umgehen.
    if ('caches' in window) {
      void caches.keys().then((keys) => Promise.all(keys.map((k) => caches.delete(k))));
    }
    window.location.href = window.location.pathname + '?reload=' + Date.now();
  };

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-zinc-950 text-zinc-100 p-6">
          <div className="max-w-lg w-full rounded-lg border border-red-900/60 bg-red-950/20 p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="size-6 text-red-300 shrink-0" />
              <h1 className="text-lg font-semibold text-red-100">
                Etwas ist schiefgegangen
              </h1>
            </div>
            <p className="text-sm text-zinc-300 leading-relaxed mb-3">
              Die App konnte nicht rendern. Das passiert manchmal nach einem
              Update, wenn der Browser noch alte Daten cached oder unerwartet
              formatierte Daten aus der Datenbank kommen.
            </p>
            <details className="rounded border border-zinc-800 bg-zinc-900/60 p-2 text-[11px] text-zinc-400 mb-4">
              <summary className="cursor-pointer text-zinc-300">Technische Details</summary>
              <pre className="mt-2 overflow-auto text-[10px] text-red-200 whitespace-pre-wrap">
                {this.state.error.message}
                {this.state.error.stack && '\n\n' + this.state.error.stack.split('\n').slice(0, 6).join('\n')}
              </pre>
            </details>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={this.handleReload}
                className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium px-4 py-2"
              >
                <RefreshCw className="size-4" />
                Seite neu laden
              </button>
              <button
                type="button"
                onClick={this.handleHardReload}
                className="inline-flex items-center gap-1.5 rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-100 text-sm font-medium px-4 py-2"
                title="Browser-Cache leeren und neu laden"
              >
                <RefreshCw className="size-4" />
                Hart neu laden (Cache leeren)
              </button>
            </div>
            <p className="mt-4 text-[10px] text-zinc-500">
              Wenn der Fehler bleibt: in der Browser-Console (F12 → Console)
              die rote Fehlermeldung notieren und mir schicken.
            </p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
